// @ts-strict-ignore
import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { Button } from "@dashboard/components/Button";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import CardTitle from "@dashboard/components/CardTitle";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import DateTime from "@dashboard/components/Date/DateTime";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import NotFoundPage from "@dashboard/components/NotFoundPage";
import { Pill } from "@dashboard/components/Pill";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import useNotifier from "@dashboard/hooks/useNotifier";
import { getStringOrPlaceholder } from "@dashboard/misc";
import { OrderDetailsDatagrid } from "@dashboard/orders/components/OrderDetailsDatagrid";
import { orderSubscriptionsListUrl, orderUrl } from "@dashboard/orders/urls";
import { cancelSubscription } from "@dashboard/orders/views/OrderSubscriptions/api";
import { mapSubscriptionStatus } from "@dashboard/orders/views/OrderSubscriptionsList/OrderSubscriptionsList.utils";
import {
  Card,
  CardContent,
  CircularProgress,
  DialogContentText,
  Link,
} from "@material-ui/core";
import { makeStyles } from "@saleor/macaw-ui";
import { Box, Divider, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useSubscriptionDetails } from "./useSubscriptionDetails";

interface OrderSubscriptionDetailsProps {
  subscriptionId: string;
}

const useStyles = makeStyles(
  theme => ({
    loader: {
      display: "flex",
      justifyContent: "center",
      padding: theme.spacing(9),
    },
    row: {
      display: "flex",
      justifyContent: "space-between",
      gap: theme.spacing(3),
      marginBottom: theme.spacing(1.5),
    },
    key: {
      color: theme.palette.text.hint,
    },
    value: {
      wordBreak: "break-word",
      textAlign: "right",
    },
    emptyText: {
      color: theme.palette.text.hint,
    },
    cancelActionRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: theme.spacing(3),
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        alignItems: "flex-start",
      },
    },
  }),
  { name: "OrderSubscriptionDetails" },
);

const getValueOrDash = (value?: string | null) => value || "—";

const parseAmount = (value?: string | null) => {
  if (!value) {
    return "—";
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : value;
};

const renderDateOrDash = (value?: string | null) => {
  if (!value) {
    return "—";
  }
  const parsed = new Date(value).getTime();
  if (Number.isNaN(parsed)) {
    return "—";
  }
  return <DateTime date={value} plain />;
};

const OrderSubscriptionDetails: React.FC<OrderSubscriptionDetailsProps> = ({
  subscriptionId,
}) => {
  const intl = useIntl();
  const notify = useNotifier();
  const classes = useStyles();
  const { loading, error, subscription, notFound, reload } =
    useSubscriptionDetails(subscriptionId);
  const [isCancelling, setIsCancelling] = React.useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = React.useState(false);

  if (!loading && !error && notFound) {
    return <NotFoundPage onBack={() => window.history.back()} />;
  }

  const status = subscription
    ? mapSubscriptionStatus(subscription.status)
    : null;
  const metadata = subscription?.metadata ?? {};
  const lines = (subscription as any)?.saleor?.lines ?? [];
  const amountText = subscription
    ? `${parseAmount(subscription.amount?.value)} ${getValueOrDash(
        subscription.amount?.currency,
      )}`
    : "—";
  const productsAmountText = `${parseAmount(
    metadata.productsAmount,
  )} ${getValueOrDash(
    metadata.shippingCurrency || subscription?.amount?.currency,
  )}`;
  const shippingAmountText = `${parseAmount(
    metadata.shippingAmount,
  )} ${getValueOrDash(
    metadata.shippingCurrency || subscription?.amount?.currency,
  )}`;
  const hasMoneyField = (value: unknown) =>
    value !== undefined && value !== null && String(value).trim() !== "";

  const hasProductsAmount = hasMoneyField(metadata.productsAmount);
  const hasShippingAmount = hasMoneyField(metadata.shippingAmount);
  const hasTotalAmount = hasMoneyField(metadata.totalAmount);

  const handleCancelSubscription = async () => {
    try {
      setIsCancelling(true);
      await cancelSubscription(subscriptionId);
      setIsCancelDialogOpen(false);
      notify({
        status: "success",
        text: intl.formatMessage({
          id: "AoIa2C",
          defaultMessage: "Subscription canceled",
        }),
      });
      reload();
    } catch (e) {
      notify({
        status: "error",
        text:
          e instanceof Error
            ? e.message
            : intl.formatMessage({
                id: "xtw9yP",
                defaultMessage: "Failed to cancel subscription",
              }),
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const cancelConfirmState: ConfirmButtonTransitionState = isCancelling
    ? "loading"
    : "default";

  return (
    <DetailPageLayout>
      <WindowTitle
        title={intl.formatMessage(
          {
            id: "AWok8H",
            defaultMessage: "Subscription {id}",
          },
          { id: subscriptionId },
        )}
      />
      <TopNav
        href={orderSubscriptionsListUrl()}
        title={intl.formatMessage(
          {
            id: "AWok8H",
            defaultMessage: "Subscription {id}",
          },
          { id: subscriptionId },
        )}
      />

      <DetailPageLayout.Content>
        {loading ? (
          <Box className={classes.loader}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box padding={6}>{error}</Box>
        ) : (
          <>
            <Card>
              <CardTitle
                title={<FormattedMessage id="yNmV/R" defaultMessage="Items" />}
              />
              <Divider />
              <CardContent>
                {!lines.length ? (
                  <Text className={classes.emptyText}>
                    <FormattedMessage
                      id="d852LC"
                      defaultMessage="No line items available for this subscription."
                    />
                  </Text>
                ) : (
                  <OrderDetailsDatagrid
                    lines={lines as any}
                    loading={false}
                    onShowMetadata={() => undefined}
                  />
                )}
              </CardContent>
            </Card>
            <CardSpacer />

            <Card>
              <CardTitle
                title={
                  <FormattedMessage
                    id="Y0dz3r"
                    defaultMessage="Subscription overview"
                  />
                }
              />
              <Divider />
              <CardContent>
                <Box className={classes.row}>
                  <Text className={classes.key}>
                    <FormattedMessage id="tzMNF3" defaultMessage="Status" />
                  </Text>
                  <Box className={classes.value}>
                    {status ? (
                      <Pill label={status.label} color={status.status} />
                    ) : (
                      "—"
                    )}
                  </Box>
                </Box>
                <Box className={classes.row}>
                  <Text className={classes.key}>
                    <FormattedMessage id="6m3w+3" defaultMessage="Interval" />
                  </Text>
                  <Text className={classes.value}>
                    {getValueOrDash(subscription?.interval)}
                  </Text>
                </Box>
                <Box className={classes.row}>
                  <Text className={classes.key}>
                    <FormattedMessage
                      id="8X6Kwp"
                      defaultMessage="Next payment"
                    />
                  </Text>
                  <Text className={classes.value}>
                    {renderDateOrDash(subscription?.nextPaymentDate)}
                  </Text>
                </Box>
                <Box className={classes.row}>
                  <Text className={classes.key}>
                    <FormattedMessage id="AbXVP4" defaultMessage="Created at" />
                  </Text>
                  <Text className={classes.value}>
                    {renderDateOrDash(subscription?.createdAt)}
                  </Text>
                </Box>
                {typeof subscription?.times === "number" && (
                  <Box className={classes.row}>
                    <Text className={classes.key}>
                      <FormattedMessage
                        id="Ep+sRk"
                        defaultMessage="Total cycles"
                      />
                    </Text>
                    <Text className={classes.value}>{subscription.times}</Text>
                  </Box>
                )}
                {typeof subscription?.timesRemaining === "number" && (
                  <Box className={classes.row}>
                    <Text className={classes.key}>
                      <FormattedMessage
                        id="qipVkU"
                        defaultMessage="Cycles remaining"
                      />
                    </Text>
                    <Text className={classes.value}>
                      {subscription.timesRemaining}
                    </Text>
                  </Box>
                )}
              </CardContent>
            </Card>
            <CardSpacer />
            <Card>
              <CardTitle
                title={
                  <FormattedMessage
                    id="UWVETE"
                    defaultMessage="Cancel subscription"
                  />
                }
              />
              <Divider />
              <CardContent>
                <Box className={classes.cancelActionRow}>
                  <Text>
                    <FormattedMessage
                      id="f3Yru8"
                      defaultMessage="Canceling the subscription stops future recurring charges."
                    />
                  </Text>
                  <Button
                    variant="secondary"
                    onClick={() => setIsCancelDialogOpen(true)}
                    disabled={
                      isCancelling ||
                      ["canceled", "cancelled"].includes(
                        String(subscription?.status ?? "").toLowerCase(),
                      )
                    }
                  >
                    <FormattedMessage id="47FYwb" defaultMessage="Cancel" />
                  </Button>
                </Box>
              </CardContent>
            </Card>
            <ActionDialog
              open={isCancelDialogOpen}
              onClose={() => setIsCancelDialogOpen(false)}
              confirmButtonState={cancelConfirmState}
              onConfirm={handleCancelSubscription}
              variant="delete"
              title={intl.formatMessage({
                id: "UWVETE",
                defaultMessage: "Cancel subscription",
              })}
              confirmButtonLabel={intl.formatMessage({
                id: "47FYwb",
                defaultMessage: "Cancel",
              })}
            >
              <DialogContentText>
                <FormattedMessage
                  id="lfR7pT"
                  defaultMessage="Are you sure you want to cancel this subscription? This action cannot be undone."
                />
              </DialogContentText>
            </ActionDialog>
          </>
        )}
      </DetailPageLayout.Content>

      <DetailPageLayout.RightSidebar>
        <Card>
          <CardTitle
            title={
              <FormattedMessage id="Ww2lud" defaultMessage="Identifiers" />
            }
          />
          <Divider />
          <CardContent>
            <Box className={classes.row}>
              <Text className={classes.key}>ID</Text>
              <Text className={classes.value}>{subscriptionId}</Text>
            </Box>
            <Box className={classes.row}>
              <Text className={classes.key}>
                <FormattedMessage id="sy+pv5" defaultMessage="Email" />
              </Text>
              <Text className={classes.value}>
                {getValueOrDash(subscription?.saleor?.userEmail)}
              </Text>
            </Box>
            <Box className={classes.row}>
              <Text className={classes.key}>
                <FormattedMessage id="5F8uSC" defaultMessage="Original order" />
              </Text>
              <Box className={classes.value}>
                {subscription?.saleor?.orderId ? (
                  <Link href={orderUrl(subscription.saleor.orderId)}>
                    {getStringOrPlaceholder(subscription.saleor.orderNumber)}
                  </Link>
                ) : (
                  "—"
                )}
              </Box>
            </Box>
            <Box className={classes.row}>
              <Text className={classes.key}>
                <FormattedMessage
                  id="90Qp0t"
                  defaultMessage="Shipping method ID"
                />
              </Text>
              <Text className={classes.value}>
                {getValueOrDash(metadata.shippingMethodId)}
              </Text>
            </Box>
          </CardContent>
        </Card>
        <CardSpacer />
        <Card>
          <CardTitle
            title={
              <FormattedMessage id="ruhEsx" defaultMessage="Payment amounts" />
            }
          />
          <Divider />
          <CardContent>
            <Box className={classes.row}>
              <Text className={classes.key}>
                <FormattedMessage
                  id="np7bns"
                  defaultMessage="Recurring amount"
                />
              </Text>
              <Text className={classes.value}>{amountText}</Text>
            </Box>
            {hasProductsAmount && (
              <Box className={classes.row}>
                <Text className={classes.key}>
                  <FormattedMessage
                    id="XpwPBA"
                    defaultMessage="Products amount"
                  />
                </Text>
                <Text className={classes.value}>{productsAmountText}</Text>
              </Box>
            )}
            {hasShippingAmount && (
              <Box className={classes.row}>
                <Text className={classes.key}>
                  <FormattedMessage
                    id="E4Ed2e"
                    defaultMessage="Shipping amount"
                  />
                </Text>
                <Text className={classes.value}>{shippingAmountText}</Text>
              </Box>
            )}
            {!hasShippingAmount && (
              <Box className={classes.row}>
                <Text className={classes.key}>
                  <FormattedMessage
                    id="6nIgUX"
                    defaultMessage="Shipping cost"
                  />
                </Text>
                <Text className={classes.value}>
                  <FormattedMessage
                    id="V//vbm"
                    defaultMessage="Determined when order is placed"
                  />
                </Text>
              </Box>
            )}
            {hasTotalAmount && (
              <Box className={classes.row}>
                <Text className={classes.key}>
                  <FormattedMessage id="dd7NBe" defaultMessage="Total amount" />
                </Text>
                <Text className={classes.value}>
                  {parseAmount(metadata.totalAmount)}{" "}
                  {getValueOrDash(subscription?.amount?.currency)}
                </Text>
              </Box>
            )}
          </CardContent>
        </Card>
      </DetailPageLayout.RightSidebar>
    </DetailPageLayout>
  );
};

export default OrderSubscriptionDetails;
